import type {FaviconConfig, TenantPublic} from '@/shared/features/tenants/tenant.model';

/**
 * Inject favicon, apple-touch-icon, and web manifest <link> tags into <head>
 * based on the resolved public tenant data. Safe to call multiple times —
 * existing elements are updated in place rather than duplicated.
 */
export function useFaviconInjection(tenant: TenantPublic): void {
   const config = tenant.favicon_config;

   setFaviconLink(config, tenant.favicon_url);

   if (config?.apple_touch_url) {
      setLink('apple-touch-icon', config.apple_touch_url);
   }

   if (config?.manifest_url) {
      setLink('manifest', config.manifest_url);
   }
}

function setFaviconLink(config: FaviconConfig | null, fallbackUrl: string | null): void {
   // Prefer .ico; fall back to 32px PNG; fall back to raw favicon_url
   const href = config?.ico_url ?? config?.png_32_url ?? fallbackUrl;
   if (! href) return;

   const type = href.endsWith('.ico') || (config?.ico_url != null && href === config.ico_url)
      ? 'image/x-icon'
      : 'image/png';

   setLink('icon', href, type);
}

function toCurrentProtocol(url: string): string {
   return url.replace(/^http:\/\//i, `${location.protocol}//`);
}

function setLink(rel: string, href: string, type?: string): void {
   let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
   if (! el) {
      el = document.createElement('link');
      el.rel = rel;
      document.head.appendChild(el);
   }
   el.href = toCurrentProtocol(href);
   if (type) el.type = type;
}
